"use client";
import React, { useEffect, useRef, useState } from "react";
import { Box, Text } from "rizzui";
import MyLoader from "./loader";
import CallRecording from "./recording";
import WaveSurfer from "wavesurfer.js";

export default function DetailsTabContent({
  isLoading,
  providerLoading,
  activityDetails,
  providerData,
  data,
}) {
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1.0);

  const recordingUrl = data?.recording_url;

  useEffect(() => {
    if (!recordingUrl || !containerEl || wavesurferRef.current) return;

    const ws = WaveSurfer.create({
      container: containerEl,
      waveColor: "#F3F8FF",
      progressColor: "#758599",
      barGap: 2,
      barWidth: 2,
      height: 22,
      hideScrollbar: true,
      url: recordingUrl,
    });

    ws.on("ready", () => setIsReady(true));
    ws.on("audioprocess", () => setCurrentTime(ws.getCurrentTime()));
    ws.on("play", () => setIsPlaying(true));
    ws.on("pause", () => setIsPlaying(false));

    wavesurferRef.current = ws;

    return () => {
      ws.destroy();
      wavesurferRef.current = null;
      setIsReady(false);
    };
  }, [recordingUrl, containerEl]);

  const handlePlayPause = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.isPlaying() ? ws.pause() : ws.play();
  };

  const handleMute = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    const newVol = ws.getVolume() === 0 ? volume : 0;
    ws.setVolume(newVol);
    if (newVol !== 0) setVolume(newVol); // preserve previous volume
  };

  if (isLoading || providerLoading) return <MyLoader />;

  return (
    <Box className="flex flex-col gap-[32px] px-[12px]">
      {activityDetails}

      <Box>
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Call summary
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {providerData?.summary
            ? providerData?.summary
            : data?.call_status === "completed"
            ? "Call summary is not available"
            : "The summary of the call will appear here once the call is completed."}
        </Text>
      </Box>

      <Box>
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px] mb-[16px]">
          Recording
        </Text>
        {recordingUrl ? (
          <CallRecording
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={isReady ? wavesurferRef.current?.getDuration?.() || 0 : 0}
            volume={wavesurferRef.current?.getVolume() ?? volume}
            onPlayPause={handlePlayPause}
            onMute={handleMute}
            waveformRef={setContainerEl}
          />
        ) : (
          <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px] -mt-[16px] mb-[16px]">
            The call recording will appear here.
          </Text>
        )}
      </Box>
    </Box>
  );
}
